import { T, useGT } from 'gt-react';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowClockwiseIcon,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useRecordNavigation } from './useRecordNavigation';
import { type DemandDetailSearch } from '../demand/demandFilters';

export function ForecastRecordNavigation({
  search,
  onNavigate,
}: {
  search: DemandDetailSearch & { rowIndex: number };
  onNavigate: (next: DemandDetailSearch) => void;
}) {
  const gt = useGT();
  const {
    index,
    offset,
    total,
    busy,
    failed,
    unavailable,
    previousTarget,
    nextTarget,
    retry,
  } = useRecordNavigation(search);

  return (
    <nav
      aria-label={gt('Forecast records', {
        context: 'forecast detail record navigation',
      })}
      className="flex flex-wrap items-center gap-2 text-xs"
    >
      <Button
        variant="outline"
        size="sm"
        disabled={unavailable || previousTarget === null}
        onClick={() => {
          if (previousTarget) onNavigate(previousTarget);
        }}
      >
        <ArrowLeftIcon className="h-4 w-4" />
        <T context="forecast detail previous record">Previous Item</T>
      </Button>
      <Button
        variant="outline"
        size="sm"
        disabled={unavailable || nextTarget === null}
        onClick={() => {
          if (nextTarget) onNavigate(nextTarget);
        }}
      >
        <ArrowRightIcon className="h-4 w-4" />
        <T context="forecast detail next record">Next Item</T>
      </Button>
      <NavigationStatus
        busy={busy}
        failed={failed}
        index={index}
        position={offset + 1}
        total={total}
      />
      {failed && (
        <Button variant="ghost" size="sm" onClick={retry}>
          <ArrowClockwiseIcon className="h-4 w-4" />
          <T context="forecast detail navigation retry">Retry</T>
        </Button>
      )}
    </nav>
  );
}

function NavigationStatus({
  busy,
  failed,
  index,
  position,
  total,
}: {
  busy: boolean;
  failed: boolean;
  index: number;
  position: number;
  /** Rows in the filtered grid; null when the server has not counted them. */
  total: number | null;
}) {
  const gt = useGT();
  return (
    <span className="text-muted-foreground" aria-live="polite">
      {busy ? (
        <T context="forecast detail navigation loading">Loading records…</T>
      ) : failed ? (
        <T context="forecast detail navigation error">
          Could not load adjacent records.
        </T>
      ) : index < 0 ? (
        <T context="forecast detail record moved after refresh">
          This item has moved or left the filtered results. Return to the demand
          grid to refresh the selection.
        </T>
      ) : (
        <span
          title={gt('Position in the filtered demand grid', {
            context: 'forecast detail current record position tooltip',
          })}
        >
          {total === null
            ? gt('Item {number}', {
                context: 'forecast detail current record position',
                number: position.toLocaleString(),
              })
            : gt('Item {number} of {total}', {
                context: 'forecast detail current record position with total',
                number: position.toLocaleString(),
                total: total.toLocaleString(),
              })}
        </span>
      )}
    </span>
  );
}
